import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import usePersistentFilters from '../../hooks/usePersistentFilters';
import {
  Users,
  Search,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Download,
  Star,
  RotateCcw,
  UserX
} from 'lucide-react';
import { api } from '../../utils/api';
import { confirmPermanentDelete } from '../../utils/confirmPermanentDelete';
import { useAuth } from '../../contexts/AuthContext';

const Passengers = () => {
  const { hasPermission } = useAuth();

  const defaultFilters = {
    search: '',
    isBlocked: '',
    // '' (all) | active | deleted: whether the passenger deleted their account
    accountStatus: '',
    gender: '',
    profile: '',
    page: 1,
    developerOnly: 'Exclude',
    limit: 10,
    sortBy: 'createdOn',
    sortOrder: 'desc'
  };

  const { filters, handleFilterChange, handlePageChange, updateFilters } = usePersistentFilters(
    defaultFilters,
    'passengers-filters'
  );

  const { data, isLoading, error, refetch } = useQuery(
    ['passengers', filters],
    () => api.get('/passengers', { params: filters }),
    {
      keepPreviousData: true
    }
  );

  const handleSort = (field) => {
    const currentFilters = filters;
    let newFilters;

    // If clicking the same field, cycle through: desc -> asc -> default (createdOn desc)
    if (currentFilters.sortBy === field) {
      if (currentFilters.sortOrder === 'desc') {
        newFilters = { ...currentFilters, sortBy: field, sortOrder: 'asc', page: 1 };
      } else {
        // Reset to default
        newFilters = { ...currentFilters, sortBy: 'createdOn', sortOrder: 'desc', page: 1 };
      }
    } else {
      // New field, start with desc
      newFilters = { ...currentFilters, sortBy: field, sortOrder: 'desc', page: 1 };
    }

    updateFilters(newFilters);
  };

  const getSortIcon = (field) => {
    if (filters.sortBy !== field) return null;
    return filters.sortOrder === 'desc' ? ' ↓' : ' ↑';
  };

  const handleBlockPassenger = async (passengerId, isBlocked) => {
    try {
      await api.put(`/passengers/${passengerId}/block`, { isBlocked });
      toast.success(`Passenger ${isBlocked ? 'blocked' : 'unblocked'} successfully`);
      refetch();
    } catch (error) {
      toast.error('Failed to update passenger status');
    }
  };

  // A deleted account cannot log in: the app answers 403 ACCOUNT_DELETED. Revoking undoes it.
  const handleRestorePassenger = async (passenger) => {
    if (window.confirm(`Restore the account of ${passenger.name || passenger.phone}? They will be able to log in again with their trip history.`)) {
      try {
        await api.put(`/passengers/${passenger._id}/restore-account`);
        toast.success('Passenger account restored');
        refetch();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to restore passenger account');
      }
    }
  };

  const handleMarkPassengerDeleted = async (passenger) => {
    if (window.confirm(`Mark the account of ${passenger.name || passenger.phone} as deleted? They will not be able to log in. You can restore it later.`)) {
      try {
        await api.put(`/passengers/${passenger._id}/mark-deleted`, {});
        toast.success('Passenger account marked as deleted');
        refetch();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to mark passenger account as deleted');
      }
    }
  };

  const handleDeletePassenger = async (passengerId) => {
    if (confirmPermanentDelete('this passenger record', 'To block login and keep the record, use "Mark as deleted" instead.')) {
      try {
        await api.delete(`/passengers/${passengerId}`);
        toast.success('Passenger deleted successfully');
        refetch();
      } catch (error) {
        toast.error('Failed to delete passenger');
      }
    }
  };

  if (error) {
    toast.error('Failed to load passengers');
  }

  const passengers = data?.data?.data?.passengers || [];
  const pagination = data?.data?.data?.pagination || {};

  const getStatusBadge = (passenger) => {
    if (passenger.isDeleted === true) {
      const when = passenger.deletedAt ? new Date(passenger.deletedAt).toLocaleDateString() : null;
      return (
        <div>
          <span className="badge badge-danger">Deleted</span>
          {when && <div className="text-xs text-gray-500 mt-1">{when}</div>}
        </div>
      );
    }
    if (passenger.isBlocked) {
      return <span className="badge badge-danger">Blocked</span>;
    }
    return <span className="badge badge-success">Active</span>;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Passengers</h1>
              <p className="page-subtitle">
                Manage passenger accounts and activities
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, phone, email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="form-label">Status</label>
              <select
                value={filters.isBlocked}
                onChange={(e) => handleFilterChange('isBlocked', e.target.value)}
                className="input"
              >
                <option value="">All Status</option>
                <option value="false">Active</option>
                <option value="true">Blocked</option>
              </select>
            </div>

            {/* Account deletion filter */}
            <div>
              <label className="form-label">Account</label>
              <select
                value={filters.accountStatus || ''}
                onChange={(e) => handleFilterChange('accountStatus', e.target.value)}
                className="input"
              >
                <option value="">All Accounts</option>
                <option value="active">Not Deleted</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>

            {/* Gender Filter */}
            <div>
              <label className="form-label">Gender</label>
              <select
                value={filters.gender}
                onChange={(e) => handleFilterChange('gender', e.target.value)}
                className="input"
              >
                <option value="">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="form-label">Profile</label>
              <select
                value={filters.profile}
                onChange={(e) => handleFilterChange('profile', e.target.value)}
                className="input"
              >
                <option value="">All Profiles</option>
                <option value="completed">Completed</option>
                <option value="incomplete">Incomplete</option>
              </select>
            </div>

            <div>
              <label className="form-label">Developer</label>
              <select
                value={filters.developerOnly}
                onChange={(e) => handleFilterChange('developerOnly', e.target.value)}
                className='input'
              >
                <option value="Exclude">Exclude Developers</option>
                <option value="Include">Include Developers</option>
                <option value="Only">Developers Only</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Passengers Table */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Passengers ({pagination.totalRecords || 0})
            </h3>
            <button className="btn btn-outline">
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : passengers.length === 0 ? (
            <div className="empty-state">
              <Users className="empty-state-icon" />
              <h3 className="empty-state-title">No passengers found</h3>
              <p className="empty-state-description">
                No passengers match your current filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Passenger</th>
                    <th className="table-header-cell">Contact</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">
                      <button
                        onClick={() => handleSort('ratingData.currentrating')}
                        className="flex items-center hover:text-primary-600 font-medium"
                      >
                        Rating{getSortIcon('ratingData.currentrating')}
                      </button>
                    </th>
                    <th className="table-header-cell">
                      <button
                        onClick={() => handleSort('totalTrips')}
                        className="flex items-center hover:text-primary-600 font-medium"
                      >
                        Trips{getSortIcon('totalTrips')}
                      </button>
                    </th>
                    <th className="table-header-cell">
                      <button
                        onClick={() => handleSort('totalSpent')}
                        className="flex items-center hover:text-primary-600 font-medium"
                      >
                        Total Spent{getSortIcon('totalSpent')}
                      </button>
                    </th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {passengers.map((passenger) => (
                    <tr key={passenger._id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-700">
                              {passenger.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {passenger.name}
                            </div>
                            <div className="text-sm text-gray-500 capitalize">
                              {passenger.gender}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">{passenger.phone || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{passenger.email || 'N/A'}</div>
                      </td>
                      <td className="table-cell">
                        {getStatusBadge(passenger)}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">
                            {passenger.ratingData?.currentrating?.toFixed(1) || '0.0'}
                          </span>
                          <span className="text-sm text-gray-500 ml-1">
                            ({passenger.ratingData?.count || 0})
                          </span>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {passenger.completedTrips || 0} / {passenger.totalTrips || 0}
                        </div>
                        <div className="text-sm text-gray-500">Completed / Total</div>
                        {(passenger.cancelledTrips || 0) > 0 && (
                          <div className="text-xs text-red-500 mt-1">
                            {passenger.cancelledTrips} cancelled
                          </div>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="text-sm font-medium text-gray-900">
                          ₹{passenger.totalSpent?.toFixed(2) || '0.00'}
                        </div>
                        {passenger.lastTripDate && (
                          <div className="text-sm text-gray-500">
                            Last trip: {new Date(passenger.lastTripDate).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/passengers/${passenger._id}`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>

                          {hasPermission('passengers', 'write') && (
                            <button
                              onClick={() => handleBlockPassenger(passenger._id, !passenger.isBlocked)}
                              className={passenger.isBlocked ? "text-success-600 hover:text-success-900" : "text-warning-600 hover:text-warning-900"}
                              title={passenger.isBlocked ? "Unblock Passenger" : "Block Passenger"}
                            >
                              {passenger.isBlocked ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : (
                                <XCircle className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {hasPermission('passengers', 'write') && passenger.isDeleted === true && (
                            <button
                              onClick={() => handleRestorePassenger(passenger)}
                              className="text-success-600 hover:text-success-900"
                              title="Restore deleted account"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                          )}

                          {hasPermission('passengers', 'delete') && passenger.isDeleted !== true && (
                            <button
                              onClick={() => handleMarkPassengerDeleted(passenger)}
                              className="text-warning-600 hover:text-warning-900"
                              title="Mark account as deleted (can be restored)"
                            >
                              <UserX className="h-4 w-4" />
                            </button>
                          )}

                          {hasPermission('passengers', 'delete') && (
                            <button
                              onClick={() => handleDeletePassenger(passenger._id)}
                              className="text-danger-600 hover:text-danger-900"
                              title="Delete permanently"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.total > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.current - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.current * pagination.limit, pagination.totalRecords)} of{' '}
            {pagination.totalRecords} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.current - 1)}
              disabled={pagination.current === 1}
              className="btn btn-outline btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.current + 1)}
              disabled={pagination.current === pagination.total}
              className="btn btn-outline btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Passengers; 